// utils/nutritionCalculations.js
//
// Pure calculation helpers extracted out of NutritionSetting.jsx.
// No React, no state — easy to unit test, and used both for the
// live BMR/TDEE preview and for the "auto-calculate" button so the
// two can never drift apart like they did before.

export const ACTIVITY_FACTORS = Object.freeze({
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
  veryActive: 1.9,
});

export const GOAL_CALORIE_ADJUSTMENT = Object.freeze({
  lose: -500,
  maintain: 0,
  gain: 500,
});

// MyFitnessPal's default macro split: a fixed percentage of total calories
// (20% protein / 50% carbs / 30% fat), NOT grams-per-kg of bodyweight.
// This replaces the old PROTEIN_G_PER_KG_BY_GOAL approach, which produced
// protein/fat/carb targets that couldn't match MFP for the same profile
// even with an identical BMR — the two methods are structurally different.
const PROTEIN_CALORIE_RATIO = 0.20;
const FAT_CALORIE_RATIO = 0.30;

const SUGAR_CALORIE_RATIO = 0.10;   // WHO: <10% of calories from free sugars
const SATURATED_FAT_CALORIE_RATIO = 0.10;
const FIBER_G_PER_1000KCAL = 14;
const SODIUM_TARGET_MG = 2000;      // flat public-health guideline, not TDEE-scaled

/**
 * Coerce a value (which may be '', null, undefined, or a string from an
 * <input>) to a finite number, falling back instead of producing NaN.
 * This is the fix for the "clear the age field and everything shows NaN"
 * bug: profile fields go through '' while the user is typing, and that
 * used to flow straight into arithmetic.
 */
export function toFiniteNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function calculateBMR({ gender, weight, height, age }) {
  const w = toFiniteNumber(weight);
  const h = toFiniteNumber(height);
  const a = toFiniteNumber(age);
  const base = 10 * w + 6.25 * h - 5 * a;
  return gender === 'male' ? base + 5 : base - 161;
}

export function calculateTDEE(bmr, activity) {
  const factor = ACTIVITY_FACTORS[activity] ?? ACTIVITY_FACTORS.moderate;
  return bmr * factor;
}

export function applyGoalAdjustment(tdee, goalType) {
  return tdee + (GOAL_CALORIE_ADJUSTMENT[goalType] ?? 0);
}

/**
 * Single source of truth for BMR / TDEE / suggested calories.
 * Used for both the read-only preview boxes and the auto-calculate button.
 */
export function calculateMetabolicStats(profile, goalType) {
  const bmr = calculateBMR(profile);
  const tdee = calculateTDEE(bmr, profile.activity);
  const suggestedCalories = applyGoalAdjustment(tdee, goalType);
  return {
    bmr: Math.round(bmr),
    tdee: Math.round(tdee),
    suggestedCalories: Math.round(suggestedCalories),
  };
}

/**
 * Macro targets from a fixed 20/50/30 (protein/carb/fat) split of
 * suggestedCalories — matches MyFitnessPal's default macro calculator.
 * Carbs absorb the rounding remainder so the three targets always sum
 * back to (approximately) suggestedCalories, same as MFP's own rounding.
 */
export function calculateMacroTargets(profile, goalType) {
  const { suggestedCalories } = calculateMetabolicStats(profile, goalType);

  const targetProtein = Math.max(0, Math.round((suggestedCalories * PROTEIN_CALORIE_RATIO) / 4));
  const targetFats = Math.max(0, Math.round((suggestedCalories * FAT_CALORIE_RATIO) / 9));
  const usedCalories = targetProtein * 4 + targetFats * 9;
  const targetCarbs = Math.max(0, Math.round((suggestedCalories - usedCalories) / 4));

  return { targetCalories: suggestedCalories, targetProtein, targetCarbs, targetFats };
}

export function calculateAdvancedMacroTargets(profile, goalType) {
  const { suggestedCalories } = calculateMetabolicStats(profile, goalType);
  return {
    targetFiber: Math.max(1, Math.round((suggestedCalories / 1000) * FIBER_G_PER_1000KCAL)),
    targetSugars: Math.max(1, Math.round((suggestedCalories * SUGAR_CALORIE_RATIO) / 4)),
    targetSodium: SODIUM_TARGET_MG,
    targetSaturatedFat: Math.max(1, Math.round((suggestedCalories * SATURATED_FAT_CALORIE_RATIO) / 9)),
  };
}

/**
 * Recompute total calories from the three macros. Used to keep
 * targetCalories in sync no matter which of protein/carbs/fats
 * the user edits — previously only the protein field did this,
 * so editing carbs or fats silently desynced the calories field.
 */
export const caloriesFromMacros = (protein, carbs, fats) => {
  const p = Number(protein) || 0;
  const c = Number(carbs) || 0;
  const f = Number(fats) || 0;
  return Math.round(p * 4 + c * 4 + f * 9);
};

export const scaleMacrosToCalories = (targetCalories, protein, carbs, fats) => {
  const p = Number(protein) || 0;
  const c = Number(carbs) || 0;
  const f = Number(fats) || 0;
  const target = Number(targetCalories) || 0;
  const currentCalories = p * 4 + c * 4 + f * 9;

  if (currentCalories <= 0) {
    // pas de base pour un ratio : répartition par défaut 30/40/30
    return {
      protein: Math.round((target * 0.3) / 4),
      carbs: Math.round((target * 0.4) / 4),
      fats: Math.round((target * 0.3) / 9),
    };
  }

  const ratio = target / currentCalories;
  return {
    protein: Math.round(p * ratio),
    carbs: Math.round(c * ratio),
    fats: Math.round(f * ratio),
  };
};